import { inject, Injectable, NgZone, signal } from '@angular/core';
import { ScrollService } from './scrollContainer.service';
import { WebSocketService } from './webSocket.service';
import { DataChatService } from './dataChat.service';
import { ChatBotTextAnimationService } from './textAnimationService.service';
import { environment } from 'src/environments/environment';
import { PlayAudioHandlerService } from './playAudioHandler.service';
import { LocalFillerPhrasesService } from './localFillerPhrases.service';
// import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { RestService } from 'src/app/services/rest.service';
import { v4 as uuidv4 } from 'uuid';

const SILENCE_THRESHOLD = 0.01;              // añadido al ruido de fondo calibrado
const MAX_SILENCE_DURATION_MS = 2500;        // auto-stop opcional
const TARGET_SR = 16000;
const CHANNELS = 1;

@Injectable({ providedIn: 'root' })
export class RecordAudioHandlerService {
  private ngZone = inject(NgZone);
  private webSocketService = inject(WebSocketService);
  private dataChat = inject(DataChatService);
  private textAnimationService = inject(ChatBotTextAnimationService);
  private playService = inject(PlayAudioHandlerService);
  private localFillerService = inject(LocalFillerPhrasesService);
  // private customRouteService = inject(CustomRouteService);
  private sessionStorageService = inject(SessionStorageService);
  private rest = inject(RestService);
  public scrollService = inject(ScrollService);

  private stream!: MediaStream;
  private micAudioContext!: AudioContext;
  private micWorkletNode!: AudioWorkletNode;
  private scriptProcessor!: ScriptProcessorNode; // fallback
  private filterNode!: BiquadFilterNode;
  private micSource?: MediaStreamAudioSourceNode;

  private chunks: Int16Array[] = [];
  private silenceStart: number | null = null;
  private isCalibrated = false;
  private tearingDown = false;
  private noiseFloor = 0;

  private animationFrameId = 0;
  private analyser!: AnalyserNode;
  private dataArray!: any; //Uint8Array;
  private gainNode!: GainNode;

  private _activeTracks = new Set<MediaStreamTrack>();
  private _abortController: AbortController | null = null;
  private beepAudio = new Audio('/assets/beep.mp3');
  private fillerInterval: any = null;
  private _isFirstAudioInteraction = false;

  public isRecording = signal(false);
  public showRecordIcon = signal(true);
  private _onVis?: () => void;


  // ---------- API PÚBLICA ----------
  public async startRecording(wavesCanvas?: HTMLCanvasElement): Promise<void> {
    if (this.isRecording())
      return;

    this.isRecording.set(true);
    // Al empezar a grabar, silenciamos al bot inmediatamente
    await this.webSocketService.cancelCompletion(false);

    // this.dataChat.hasHistory.set(true);
    this.chunks.length = 0;
    this._abortController?.abort();
    this._abortController = new AbortController();
    const startSignal = this._abortController.signal;

    try {
      // Iniciar muletillas locales (mientras el usuario habla)
      if (this.fillerInterval) clearInterval(this.fillerInterval);

      // Frase de bienvenida inmediata (Solo la primera vez)
      // this._isFirstAudioInteraction = !this.localFillerService.hasSaidWelcome();
      // this.localFillerService.sayWelcome();

      const userInteraction = this.dataChat.messages()?.filter(e => e.role === 'user')?.length;
      if (!userInteraction || this.localFillerService.hasSaidWelcome())
        await this.localFillerService.playWelcome();
      
      await this.enableMic(startSignal);
      await this.setupWaves(wavesCanvas!);
      this.attachVisibilityHandlers();

      const delay = 6000;

      setTimeout(() => {
        if (this.isRecording()) {
          this.localFillerService.sayListeningFiller();

          // Siguientes cada 6 segundos
          this.fillerInterval = setInterval(() => {
            if (this.isRecording()) {
              this.localFillerService.sayListeningFiller();
            }
          }, delay);
        }
      }, delay);

    } catch (error) {
      console.error('[startRecording] Error:', error);
      await this.disableMic();
      throw error;
    }
  }

  private _isProcessingStop = false;

  public async stopRecording(): Promise<void> {
    if (this._isProcessingStop) return;
    this._isProcessingStop = true;

    // Detenemos las muletillas locales de escucha
    if (this.fillerInterval) {
      clearInterval(this.fillerInterval);
      this.fillerInterval = null;
    }

    // NO usar sayThinkingFiller: el backend envía el filler traducido por pubsub al tts/stream.
    // Si lo reprodujéramos aquí (tts/simple) se oiría duplicado y en español, mezclado con la respuesta.

    this.dataChat.isStreaming.set(true);
    this.dataChat.chatIsTyping.set(true)
    this._abortController?.abort();

    const chunks = this.chunks.slice();
    const noiseFloor = this.noiseFloor;
    await this.disableMic();

    try {
      const merged = this.mergeChunks(chunks);
      const durationSec = merged.length / TARGET_SR;

      // const hasVoiceDetected = this.hasVoice(merged, TARGET_SR, noiseFloor);

      // console.log(`[stopRecording] Duration: ${durationSec.toFixed(2)}s, hasVoice: ${hasVoiceDetected}`);

      // Dejamos pasar audios incluso si no detectamos voz clara para que el backend 
      // pueda responder amablemente con el mensaje "upss... no pude escucharte".
      // if (durationSec < 0.1) {
      //   this.dataChat.isStreaming.set(false);
      //   this.dataChat.chatIsTyping.set(false);
      //   console.warn(`🔇 Audio extremadamente corto (${durationSec.toFixed(2)}s), ignorando.`);
      //   return;
      // }

      if (!this.hasVoice(merged, TARGET_SR, noiseFloor) || durationSec < 0.75) {
        this.dataChat.isStreaming.set(false);
        this.dataChat.chatIsTyping.set(false);
        console.warn('🔇 Audio silencioso, no se envía');
        return;
      }

      const wav = this.convertPCMToWav(merged, TARGET_SR, CHANNELS);
      const blob = new Blob([wav], { type: 'audio/wav' });

      // Enviar audio via HTTP POST (igual que Chat Tester)
      await this.sendAudioViaHttp(blob);
    }
    catch (err) {
      console.error('[stopRecording] Falló la preparación/envío del audio:', err);
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsTyping.set(false);
      throw err;
    }
    finally {
      this._isProcessingStop = false;
      this._abortController = null;
      this.chunks.length = 0;
    }
  }

  /**
   * Envía el audio al backend vía HTTP POST (igual que Chat Tester).
   * El backend transcribe con Gemini y procesa con LangGraph.
   */
  private async sendAudioViaHttp(blob: Blob): Promise<void> {
    const completionId = uuidv4();
    this.webSocketService.trackCompletionId(completionId);
    const subdomain = this.rest.subDomain || '';
    const location = {};
    const formData = new FormData();
    formData.append('file', blob, 'audio.wav');
    formData.append('user_id', this.dataChat.token() || 'anonymous');
    formData.append('service', this.dataChat.id()?.toString() || '34');
    formData.append('is_first_voice_message', this._isFirstAudioInteraction ? 'true' : 'false');
    formData.append('completion_id', completionId);
    formData.append('subdomain', subdomain);
    formData.append('app', subdomain);
    formData.append('location', JSON.stringify(location));
    const browserLanguage = this.getBrowserLanguage();
    formData.append('language', browserLanguage);
    const routeToken = this.getRouteToken();
    const hasActiveRoute = this.hasActiveRoute();
    if (routeToken) {
      formData.append('route_token', routeToken);
    }
    formData.append('route_active', hasActiveRoute ? 'true' : 'false');

    try {
      // Llamar al endpoint /api/chat/audio (chat router tiene prefix="/api")
      const apiUrl = `${environment.apiAssistantUrlBase}/api/chat/audio`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      console.log('[sendAudioViaHttp] Response:', data);

      // Mostrar transcripción como mensaje del usuario
      if (data.transcription && data.transcription.trim()) {
        this.textAnimationService.createUserMessage(data.transcription);
      }

      // El backend ya encoló el mensaje en Redis
      // La respuesta llegará por el WebSocket existente
      if (data.message_id) {
        // Registrar el ID como 'text' porque el Worker envía text_content (no audio_content)
        //TODO: check this
        this.webSocketService.trackCompletionId(data.message_id, 'text');

        const fallbackLang = (this.dataChat.lang() || browserLanguage || 'es').toLowerCase().slice(0, 2);
        const audioUrl = `${environment.apiAssistantUrlBase}/tts/stream/${data.message_id}?language=${encodeURIComponent(fallbackLang)}`;
        console.log('[sendAudioViaHttp] Playing TTS URL:', audioUrl);

        this.localFillerService.playTypingSound();
        this.playService.playExternalAudio(audioUrl);
      }
      console.log('[sendAudioViaHttp] Message queued, ID:', data.message_id);
    } catch (error) {
      console.error('[sendAudioViaHttp] Error:', error);
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsTyping.set(false);
      throw error;
    }
  }

  private getBrowserLanguage(): string {
    if (typeof navigator === 'undefined') return 'es';

    if (navigator.languages?.length) {
      return navigator.languages[0] || 'es';
    }

    return navigator.language || 'es';
  }

  private getRouteToken(): string | undefined {
    return;

    // return this.customRouteService.token()
    //   ?? this.sessionStorageService.getItem<string>('customRouteToken')
    //   ?? undefined;
  }

  private hasActiveRoute(): boolean {
    return;
    // return (this.customRouteService.customRouteData()?.dailyRoutes?.length ?? 0) > 0;
  }

  // ---------- AUDIO: ENABLE/DISABLE ----------
  private async enableMic(signal?: AbortSignal) {
    if (this.micAudioContext)
      await this.disableMic();

    if (signal?.aborted)
      return;

    // Permisos & constraints (best-effort en móviles)
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: { ideal: CHANNELS },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
      },
      video: false,
    });

    if (signal?.aborted) {
      stream.getTracks().forEach(t => t.stop());
      return;
    }

    this.stream = stream;
    this.addStream(this.stream);
    this.isRecording.set(true);

    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.micAudioContext = new Ctx({ latencyHint: 'interactive' });
    if (signal?.aborted) {
      await this.disableMic();
      return;
    }

    // Resumen: crear nodos comunes
    this.filterNode = this.micAudioContext.createBiquadFilter();
    this.filterNode.type = 'highpass';
    this.filterNode.frequency.value = 300;
    this.filterNode.Q.value = 0.707;

    this.gainNode = this.micAudioContext.createGain();
    this.gainNode.gain.setValueAtTime(0, this.micAudioContext.currentTime);

    this.analyser = this.micAudioContext.createAnalyser();
    this.analyser.fftSize = 1024; // más ligero que 2048
    this.analyser.smoothingTimeConstant = 0.7;

    this.micSource = this.micAudioContext.createMediaStreamSource(this.stream);
    this.micSource.connect(this.filterNode);
    this.filterNode.connect(this.gainNode);
    this.gainNode.connect(this.analyser);

    // Fade-in ~80ms
    const t0 = this.micAudioContext.currentTime + 0.01;
    this.gainNode.gain.setValueAtTime(0, t0);
    this.gainNode.gain.linearRampToValueAtTime(1, t0 + 0.08);

    await this.micAudioContext.resume();
    if (signal?.aborted) {
      await this.disableMic();
      return;
    }
    // Branch: AudioWorklet o fallback ScriptProcessor
    if ('audioWorklet' in this.micAudioContext) {
      try {
        await this.micAudioContext.audioWorklet.addModule('assets/audio-worklet/pcm-worklet.js');
        if (signal?.aborted) {
          await this.disableMic();
          return;
        }

        this.micWorkletNode = new AudioWorkletNode(this.micAudioContext, 'pcm-worklet', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          processorOptions: { bufferSize: 512, targetSampleRate: TARGET_SR },
        });
        this.micWorkletNode.port.postMessage({ type: 'reset' });

        // Conexiones
        this.gainNode.connect(this.micWorkletNode);

        // Calibración + append chunks
        let calibrationFrames = 0;
        let calibrationSum = 0;

        this.micWorkletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
          if (!this.isRecording())
            return;

          const pcmChunk = new Int16Array(event.data);

          // calibración de ruido de fondo
          if (!this.isCalibrated) {
            const rms = this.getRMS(pcmChunk);
            calibrationSum += rms;
            calibrationFrames++;
            if (calibrationFrames >= 30) {
              this.noiseFloor = calibrationSum / calibrationFrames;
              this.isCalibrated = true;
              // console.log('📏 Ruido de fondo:', this.noiseFloor.toFixed(5));
            }
            return;
          }

          // guarda chunk
          this.chunks.push(pcmChunk);

          // VAD + histéresis
          const rms = this.getRMS(pcmChunk);
          this.checkSilence(rms);
        };
      } catch (e) {
        console.warn('[AudioWorklet] No disponible o error al cargar. Usando fallback ScriptProcessor.', e);
        this.setupScriptProcessorFallback();
      }
    } else {
      console.log("DEBUG: audioWorkletFallback");
      if (signal?.aborted) {
        await this.disableMic();
        return;
      }
      this.setupScriptProcessorFallback();
    }
  }

  private setupScriptProcessorFallback() {
    // Tamaño 1024: compromiso CPU/latencia
    this.scriptProcessor = this.micAudioContext.createScriptProcessor(1024, CHANNELS, CHANNELS);
    this.gainNode.connect(this.scriptProcessor);
    // Evita que Safari corte el grafo si no hay salida (opcional):
    this.scriptProcessor.connect(this.micAudioContext.destination);

    let calibrationFrames = 0;
    let calibrationSum = 0;
    const inSr = this.micAudioContext.sampleRate;

    this.scriptProcessor.onaudioprocess = (ev: AudioProcessingEvent) => {
      if (!this.isRecording() || !this.scriptProcessor)
        return;

      const input = ev.inputBuffer.getChannelData(0);

      // Calibración sobre float32 antes de convertir
      if (!this.isCalibrated) {
        const rmsF32 = this.getRMSf32(input);
        calibrationSum += rmsF32;
        calibrationFrames++;
        if (calibrationFrames >= 30) {
          this.noiseFloor = calibrationSum / calibrationFrames;
          this.isCalibrated = true;
        }
        return;
      }

      // Re-muestreo a 16k y conversión a Int16
      const down = this.downsampleBuffer(input, inSr, TARGET_SR); // Float32
      const pcm = this.float32ToInt16(down);                      // Int16Array
      this.chunks.push(pcm);

      const rms = this.getRMS(pcm);
      this.checkSilence(rms);     // ✅ ahora pasamos number
    };
  }

  private hasVoice(pcm: Int16Array, sr = 16000, noiseFloor = 0): boolean {
    const win = Math.floor(0.02 * sr);   // 20 ms
    const hop = win;
    const delta = 0.003;
    const thr = Math.min(noiseFloor + delta, 0.02);
    let voiced = 0, total = 0;
    for (let i = 0; i + win <= pcm.length; i += hop) {
      const rms = this.getRMS(pcm.subarray(i, i + win));
      if (rms > thr) voiced++;
      total++;
    }
    return total > 0 && (voiced / total) >= 0.1;
  }

  public async disableMic() {
    if (this.tearingDown)
      return;

    this.isRecording.set(false);
    this.tearingDown = true;
    this.detachVisibilityHandlers();
    this.disconnectWavesAudio();

    try {
      // Fade-out ~80ms
      try {
        const t1 = this.micAudioContext?.currentTime ?? 0;
        if (this.gainNode && this.micAudioContext && Number.isFinite(t1)) {
          this.gainNode.gain.cancelScheduledValues(t1);
          this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, t1);
          this.gainNode.gain.linearRampToValueAtTime(0, t1 + 0.08);
          await new Promise(r => setTimeout(r, 90));
        }
      } catch { }

      // Desenchufes
      try { this.micWorkletNode?.port && (this.micWorkletNode.port.onmessage = null); } catch { }
      try { this.micWorkletNode?.port?.close(); } catch { }
      try { this.micWorkletNode?.disconnect(); } catch { }
      try { this.scriptProcessor?.disconnect(); } catch { }
      try { this.filterNode?.disconnect(); } catch { }
      try { this.micSource?.disconnect(); } catch { }
      try { this.gainNode?.disconnect(); } catch { }

      // Tracks
      this._activeTracks.forEach(t => { try { t.stop(); } catch { } });
      this._activeTracks.clear();

      // AudioContext
      try { await this.micAudioContext?.suspend(); } catch { }
      try { await this.micAudioContext?.close(); } catch { }
    }
    finally {
      // Reset duro de referencias/estados
      this.micAudioContext = undefined as any;
      this.micWorkletNode = undefined as any;
      this.scriptProcessor = undefined as any;
      this.micSource = undefined;
      this.filterNode = undefined as any;
      this.gainNode = undefined as any;
      this.stream = undefined as any;

      this.isCalibrated = false;
      this.tearingDown = false;
      this.silenceStart = null;
      this.noiseFloor = 0;
      this._abortController = null;
    }
  }

  // ---------- UTIL AUDIO ----------
  private getRMS(pcm: Int16Array): number {
    let s = 0;
    for (let i = 0; i < pcm.length; i++) {
      const v = pcm[i];
      s += v * v;
    }

    return Math.sqrt(s / pcm.length) / 32768;
  }

  private getRMSf32(buf: Float32Array): number {
    let s = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i];
      s += v * v;
    }

    return Math.sqrt(s / buf.length);
  }

  private float32ToInt16(input: Float32Array): Int16Array {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    return out;
  }

  // Downsample simple (sinc/LPF sería mejor; esto funciona bien para voz)
  private downsampleBuffer(input: Float32Array, inSampleRate: number, outSampleRate: number): Float32Array {
    if (outSampleRate === inSampleRate)
      return input.slice();

    if (outSampleRate > inSampleRate)
      return input.slice(); // Upsample no esperado; devolvemos copia

    const ratio = inSampleRate / outSampleRate;
    const outLen = Math.floor(input.length / ratio);
    const output = new Float32Array(outLen);

    let inIndex = 0;
    for (let i = 0; i < outLen; i++) {
      const nextInIndex = Math.round((i + 1) * ratio);
      // promedio simple para antialiasing básico
      let sum = 0;
      let count = 0;
      while (inIndex < nextInIndex && inIndex < input.length) {
        sum += input[inIndex++];
        count++;
      }
      output[i] = count ? sum / count : 0;
    }
    return output;
  }

  private mergeChunks(chunks: Int16Array[]): Int16Array {
    if (!chunks.length)
      return new Int16Array(0);

    const totalLength = chunks.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  private convertPCMToWav(pcmData: Int16Array, sampleRate: number, numChannels: number): ArrayBuffer {
    const bytesPerSample = 2; // 16-bit
    const blockAlign = numChannels * bytesPerSample;
    const buffer = new ArrayBuffer(44 + pcmData.length * bytesPerSample);
    const view = new DataView(buffer);
    let offset = 0;

    // RIFF
    this.writeString(view, offset, 'RIFF'); offset += 4;
    view.setUint32(offset, 36 + pcmData.length * bytesPerSample, true); offset += 4;
    this.writeString(view, offset, 'WAVE'); offset += 4;

    // fmt
    this.writeString(view, offset, 'fmt '); offset += 4;
    view.setUint32(offset, 16, true); offset += 4;
    view.setUint16(offset, 1, true); offset += 2; // PCM
    view.setUint16(offset, numChannels, true); offset += 2;
    view.setUint32(offset, sampleRate, true); offset += 4;
    view.setUint32(offset, sampleRate * blockAlign, true); offset += 4;
    view.setUint16(offset, blockAlign, true); offset += 2;
    view.setUint16(offset, 16, true); offset += 2;

    // data
    this.writeString(view, offset, 'data'); offset += 4;
    view.setUint32(offset, pcmData.length * bytesPerSample, true); offset += 4;

    // PCM
    for (let i = 0; i < pcmData.length; i++) {
      view.setInt16(offset, pcmData[i], true);
      offset += 2;
    }
    return buffer;
  }

  private writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  }

  private blobToBase64(blob: Blob, signal?: AbortSignal): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const onAbort = () => { try { reader.abort(); } catch { } reject(new DOMException('Aborted', 'AbortError')); };
      if (signal) {
        if (signal.aborted) return onAbort();
        signal.addEventListener('abort', onAbort, { once: true });
      }
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private checkSilence(rms: number) {
    const dynamicThreshold = this.noiseFloor + SILENCE_THRESHOLD;
    const release = Math.max(dynamicThreshold * 1.3, dynamicThreshold + 0.003);
    const now = Date.now();

    if (rms < dynamicThreshold) {
      if (this.silenceStart === null) this.silenceStart = now;
      // Auto-stop opcional:
      // Si es la primera interacción (bienvenida larga), damos mucho margen (25s) para que el usuario escuche.
      // Si no, usamos el umbral estándar (2.5s).
      const maxSilence = MAX_SILENCE_DURATION_MS; //this._isFirstAudioInteraction ? 10000 : MAX_SILENCE_DURATION_MS;

      if (this.silenceStart !== null && (now - this.silenceStart > maxSilence)) {
        console.log('🛑 Silencio prolongado. Deteniendo...');
        this.stopRecording().catch(console.error);
      }
    } else if (rms > release) {
      this.silenceStart = null;
    }
  }

  private addStream(stream: MediaStream) {
    stream.getTracks().forEach(t => this._activeTracks.add(t));
  }

  // ---------- WAVES / VISUAL ----------
  async setupWaves(canvas?: HTMLCanvasElement) {
    if (!canvas || !this.analyser)
      return;

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.drawSoundWave(canvas);
  }

  private drawSoundWave(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx)
      return;

    const minHeight = 4;
    const barSpacing = 6;
    let lastTs = 0;

    this.ngZone.runOutsideAngular(() => {
      const loop = (ts?: number) => {
        this.animationFrameId = requestAnimationFrame(loop);

        // throttle ~30fps
        if (ts && ts - lastTs < 33) return;
        lastTs = ts ?? 0;

        this.analyser?.getByteFrequencyData(this.dataArray);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const totalBars = Math.floor(canvas.width / (barSpacing + 1));
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const halfBars = Math.floor(totalBars / 2);

        for (let i = 0; i < halfBars; i++) {
          const dataIndex = Math.floor(i * (this.dataArray.length / halfBars));
          const v = this.dataArray[dataIndex] / 255.0;
          const barHeight = Math.max(minHeight, v * (canvas.height / 2));
          ctx.fillStyle = this.dataChat.chatStyles.bot_border_color;
          const xLeft = centerX - i * (barSpacing + 1);
          const xRight = centerX + i * (barSpacing + 1);
          ctx.fillRect(xLeft, centerY - barHeight, 2, barHeight * 2);
          ctx.fillRect(xRight, centerY - barHeight, 2, barHeight * 2);
        }
      };
      this.animationFrameId = requestAnimationFrame(loop);
    });
  }

  private disconnectWavesAudio() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = 0;
    }
    try { this.analyser?.disconnect(); } catch { }
    this.analyser = undefined as any;
    this.dataArray = undefined as any;
  }

  // ---------- UX / COMPAT ----------
  private attachVisibilityHandlers() {
    this.detachVisibilityHandlers();
    this._onVis = async () => {
      if (!this.micAudioContext) return;
      try {
        if (document.visibilityState === 'visible' && this.micAudioContext.state === 'suspended')
          await this.micAudioContext.resume();
      } catch { }
    };
    document.addEventListener('visibilitychange', this._onVis, { passive: true });
  }

  private detachVisibilityHandlers() {
    if (this._onVis) {
      document.removeEventListener('visibilitychange', this._onVis);
      this._onVis = undefined;
    }
  }

  // ---------- OPCIONAL: beep (recuerda gesto de usuario y HTTPS) ----------
  public playBeepToRecord() {
    try {
      const audio = new Audio('/assets/beep.mp3');
      // En iOS solo funcionará tras un gesto del usuario
      audio.play().catch(() => {/* ignorar */ });
    } catch { }
  }
}
